import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useAdminPartnerCodes } from "@/hooks/useAdminPartnerCodes";
import type { Tables } from "@/integrations/supabase/types";

type PartnerCode = Tables<"partner_codes">;

const formSchema = z.object({
  code: z.string().min(1, "Code is required").toUpperCase(),
  partner_name: z.string().min(1, "Partner name is required"),
  description: z.string().optional(),
  discount_type: z.enum(["credits", "percentage"]),
  credits_value: z.coerce.number().min(0).optional(),
  discount_percentage: z.coerce.number().min(1).max(100).optional(),
  purchase_price: z.coerce.number().min(0).optional(),
  checkout_preview_text: z.string().optional(),
  max_uses: z.coerce.number().min(1).optional().nullable(),
  max_uses_per_user: z.coerce.number().min(1).optional().nullable(),
  max_uses_per_month: z.coerce.number().min(1).optional().nullable(),
  valid_from: z.date().optional().nullable(),
  valid_until: z.date().optional().nullable(),
  requires_purchase: z.boolean().default(false),
  is_active: z.boolean().default(true),
  applies_to_tags: z.string().optional(),
  tier_slug: z.string().optional().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

interface PartnerCodeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  partnerCode?: PartnerCode;
}

const PartnerCodeForm = ({ open, onOpenChange, mode, partnerCode }: PartnerCodeFormProps) => {
  const { createPartnerCode, updatePartnerCode, isCreating, isUpdating } = useAdminPartnerCodes();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: partnerCode
      ? {
          code: partnerCode.code,
          partner_name: partnerCode.partner_name,
          description: partnerCode.description || "",
          discount_type: (partnerCode.discount_type as "credits" | "percentage") || "credits",
          credits_value: partnerCode.credits_value || 0,
          discount_percentage: partnerCode.discount_percentage || undefined,
          purchase_price: partnerCode.purchase_price || undefined,
          checkout_preview_text: partnerCode.checkout_preview_text || "",
          max_uses: partnerCode.max_uses || null,
          max_uses_per_user: partnerCode.max_uses_per_user || null,
          max_uses_per_month: partnerCode.max_uses_per_month || null,
          valid_from: partnerCode.valid_from ? new Date(partnerCode.valid_from) : null,
          valid_until: partnerCode.valid_until ? new Date(partnerCode.valid_until) : null,
          requires_purchase: partnerCode.requires_purchase || false,
          is_active: partnerCode.is_active ?? true,
          applies_to_tags: partnerCode.applies_to_tags?.join(", ") || "",
          tier_slug: (partnerCode as any).tier_slug || null,
        }
      : {
          discount_type: "credits",
          is_active: true,
          requires_purchase: false,
        },
  });

  const discountType = form.watch("discount_type");
  const requiresPurchase = form.watch("requires_purchase");

  const onSubmit = (values: FormValues) => {
    const applies_to_tags = values.applies_to_tags
      ? values.applies_to_tags.split(",").map((tag) => tag.trim()).filter(Boolean)
      : null;

    const data = {
      code: values.code,
      partner_name: values.partner_name,
      description: values.description || null,
      discount_type: values.discount_type,
      credits_value: values.discount_type === "credits" ? values.credits_value || 0 : 0,
      discount_percentage: values.discount_type === "percentage" ? values.discount_percentage || null : null,
      purchase_price: values.purchase_price || null,
      checkout_preview_text: values.checkout_preview_text || null,
      max_uses: values.max_uses || null,
      max_uses_per_user: values.max_uses_per_user || null,
      max_uses_per_month: values.max_uses_per_month || null,
      valid_from: values.valid_from?.toISOString() || null,
      valid_until: values.valid_until?.toISOString() || null,
      requires_purchase: values.requires_purchase,
      is_active: values.is_active,
      applies_to_tags,
      tier_slug: values.tier_slug || null,
    } as any;

    if (mode === "create") {
      createPartnerCode(data, {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
      });
    } else if (partnerCode) {
      updatePartnerCode(
        { id: partnerCode.id, updates: data },
        {
          onSuccess: () => {
            onOpenChange(false);
          },
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create Partner Code" : "Edit Partner Code"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={form.formState.errors.code ? "text-destructive" : ""}>
                      Code *
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="WELCOME10"
                        disabled={mode === "edit"}
                        className="uppercase"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partner_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={form.formState.errors.partner_name ? "text-destructive" : ""}>
                      Partner Name *
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Partner Company" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Optional description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Discount Type */}
            <FormField
              control={form.control}
              name="discount_type"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className={form.formState.errors.discount_type ? "text-destructive" : ""}>
                    Discount Type *
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex gap-4"
                    >
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="credits" />
                        </FormControl>
                        <FormLabel className="font-normal">Credits</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="percentage" />
                        </FormControl>
                        <FormLabel className="font-normal">Percentage</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Type-specific fields */}
            {discountType === "credits" && (
              <FormField
                control={form.control}
                name="credits_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={form.formState.errors.credits_value ? "text-destructive" : ""}>
                      Credits Value *
                    </FormLabel>
                    <FormControl>
                      <Input type="number" {...field} placeholder="10" min="0" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {discountType === "percentage" && (
              <FormField
                control={form.control}
                name="discount_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={form.formState.errors.discount_percentage ? "text-destructive" : ""}>
                      Discount Percentage *
                    </FormLabel>
                    <FormControl>
                      <Input type="number" {...field} placeholder="10" min="1" max="100" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="checkout_preview_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Checkout Preview Text</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Optional text shown at checkout" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Usage Limits */}
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="max_uses"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Uses (Total)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Unlimited"
                        min="1"
                      />
                    </FormControl>
                    <FormDescription>Leave empty for unlimited</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_uses_per_user"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Per User</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Unlimited"
                        min="1"
                      />
                    </FormControl>
                    <FormDescription>Leave empty for unlimited</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_uses_per_month"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Per Month</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Unlimited"
                        min="1"
                      />
                    </FormControl>
                    <FormDescription>Per user, per month</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Validity Period */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Valid From</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-50 bg-popover border shadow-lg" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onSelect={field.onChange}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="valid_until"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Valid Until</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-50 bg-popover border shadow-lg" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onSelect={field.onChange}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Options */}
            <FormField
              control={form.control}
              name="requires_purchase"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Requires Purchase</FormLabel>
                    <FormDescription>
                      User must complete a purchase to activate this code
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {requiresPurchase && (
              <FormField
                control={form.control}
                name="purchase_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Price (£)</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} placeholder="9.99" min="0" step="0.01" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="applies_to_tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applies to Tags</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="tag1, tag2, tag3" />
                  </FormControl>
                  <FormDescription>Comma-separated list of event tags</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />


            <FormField
              control={form.control}
              name="tier_slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Membership Tier</FormLabel>
                  <FormControl>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    >
                      <option value="">None</option>
                      <option value="friends">Nimi Friends</option>
                      <option value="plus">Nimi Plus</option>
                      <option value="all_access">Nimi All Access</option>
                    </select>
                  </FormControl>
                  <FormDescription>Auto-managed by membership system when set</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active</FormLabel>
                    <FormDescription>
                      Whether this code can be used by users
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isCreating || isUpdating}
                variant="secondary"
              >
                {mode === "create" ? "Create Code" : "Update Code"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerCodeForm;
